import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mirror the user to localStorage so per-user preferences (lib/persisted.js)
  // can scope by user.id without prop drilling.
  useEffect(() => {
    if (user && user.id) localStorage.setItem("hv:user", JSON.stringify({ id: user.id }));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    // Hard escape hatch: if /auth/me hasn't returned in 3 seconds, stop
    // showing "Loading…" — drop straight to the login form so the user
    // can try again. The previous 8s was painful on iOS PWA standalone
    // where a stalled /auth/me would freeze the whole app. The axios
    // call itself is also capped at 5s explicitly below. Locked 2026-05.
    const escapeTimer = setTimeout(() => {
      if (cancelled) return;
      const cached = localStorage.getItem("hv:user");
      if (cached) {
        // We DON'T trust the cached doc to grant access — Protected will
        // redirect to /login unless the bearer is still valid. But by
        // setting `loading=false` we let the router actually render
        // SOMETHING instead of the indefinite spinner.
        try {
          // eslint-disable-next-line no-console
          console.warn("[Auth] /auth/me slow — falling back to login redirect");
        } catch {
          /* ignore */
        }
      }
      setUser((u) => (u === null ? false : u));
      setLoading(false);
    }, 12_000);

    (async () => {
      // [Iter 265] Cold-start tolerance for /auth/me.
      // Production containers scale to zero when idle; the first
      // request after wake takes 5-30s. A single /auth/me failure was
      // immediately evicting the user back to /login — which on top of
      // the SyncButton's window.location.reload() turned every cold-
      // start into "PDF didn't show → tap Sync → kicked to login →
      // can't log in for 5 minutes". Fix: retry /auth/me up to 3 times
      // with backoff before declaring the session dead. We DON'T want
      // to retry forever (real auth failures must surface), so 3
      // attempts over ~10s. If localStorage still has a token, keep
      // the cached user object visible during the retry window so the
      // UI doesn't flash to login.
      const cachedUserRaw = localStorage.getItem("hv:user");
      const hasToken = !!localStorage.getItem("hv_token");
      let cachedUser = null;
      if (cachedUserRaw && hasToken) {
        try { cachedUser = JSON.parse(cachedUserRaw); } catch { /* ignore */ }
      }
      const attempts = hasToken ? 3 : 1;
      const delays = [0, 2_000, 4_000]; // attempt timings
      for (let i = 0; i < attempts; i++) {
        if (cancelled) return;
        if (i > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
          if (cancelled) return;
        }
        try {
          const { data } = await api.get("/auth/me", { timeout: 5_000 });
          if (cancelled) return;
          setUser(data);
          clearTimeout(escapeTimer);
          setLoading(false);
          return;
        } catch (err) {
          // If it's a clear 401/403, the session is genuinely invalid —
          // don't waste retries.
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            break;
          }
          // 5xx / timeout / network → retry.
          if (i === attempts - 1) {
            // Last attempt failed; fall through to eviction.
          }
        }
      }
      if (cancelled) return;
      // All attempts failed. If we have a cached user + token, keep
      // them visible (Protected routes will redirect on the NEXT
      // request that comes back 401). Otherwise show login.
      if (cachedUser) {
        setUser(cachedUser);
      } else {
        setUser(false);
      }
      clearTimeout(escapeTimer);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      clearTimeout(escapeTimer);
    };
  }, []);

  const login = async (email, password) => {
    // Iter 175 (2026-02-20, Dirk on production) — handle cold-start
    // timeouts. Production containers scale to zero when idle; the
    // first request after idle waits 15-30s for the container to wake.
    // Strategy:
    //   1. Timeout per attempt: 30s (covers cold start + bcrypt verify).
    //   2. If the FIRST attempt times out, the container is now warm
    //      from our pre-warm probe; we automatically retry ONCE.
    //   3. The retry's timeout is 15s (warm container should respond
    //      well within that). On a second timeout we surface the error
    //      properly so the user knows to check connectivity.
    const TIMEOUT_FIRST = 30_000;
    const TIMEOUT_RETRY = 15_000;

    const _attempt = (timeoutMs) =>
      api.post("/auth/login", { email, password }, { timeout: timeoutMs });

    let resp;
    try {
      resp = await _attempt(TIMEOUT_FIRST);
    } catch (e) {
      // Only auto-retry on timeout / network failure — NOT on 401 / 403
      // (we don't want to lock people out by retrying wrong passwords).
      const isTimeout = e?.code === "ECONNABORTED" || !e?.response;
      if (!isTimeout) throw e;
      resp = await _attempt(TIMEOUT_RETRY);
    }
    const { data } = resp;
    // Iter 426 (2026-05-11) — Multi-customer portal login. Backend
    // returns { scope_required, email, customers } and NO token when
    // a portal user is a member of multiple customers. Return a
    // special sentinel so Login.jsx renders the picker instead of
    // navigating to /portal.
    if (data?.scope_required) {
      return {
        scope_required: true,
        email: data.email || email,
        customers: data.customers || [],
        _password: password,  // held in memory only, passed to loginScope
      };
    }
    if (data.token) localStorage.setItem("hv_token", data.token);
    setUser(data.user);
    // Reset the Hidro "fold to history on first open" flag so it triggers
    // again on the new login session.
    sessionStorage.removeItem("hidro_post_login_seen");
    return data.user;
  };

  // Iter 426 — Second step of the multi-customer portal login. Called
  // after `login()` returned `{ scope_required: true, ... }` and the
  // user picked a customer card. Password is held only in the picker
  // state — never persisted.
  const loginScope = async (email, password, customerId) => {
    const { data } = await api.post(
      "/auth/login/scope",
      { email, password, customer_id: customerId },
      { timeout: 30_000 },
    );
    if (data.token) localStorage.setItem("hv_token", data.token);
    setUser(data.user);
    sessionStorage.removeItem("hidro_post_login_seen");
    return data.user;
  };

  const claimPassword = async (email, newPassword) => {
    const { data } = await api.post("/auth/claim-password", {
      email,
      new_password: newPassword,
    });
    if (data.token) localStorage.setItem("hv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("hv_token");
    localStorage.removeItem("hv:user");
    setUser(false);
  };

  // 2026-02-18 — One-tap demo login for the public /_gemini showcase.
  // Returns the demo user so callers can navigate after login.
  const demoLogin = async () => {
    const { data } = await api.post("/demo/login", {}, { timeout: 8_000 });
    if (data.token) localStorage.setItem("hv_token", data.token);
    setUser(data.user);
    sessionStorage.removeItem("hidro_post_login_seen");
    return data.user;
  };

  const hasPermission = (key) => {
    if (!user) return false;
    // Owner is the business owner — ALWAYS bypasses every permission gate,
    // regardless of whether their permissions object is hydrated.  This
    // guarantees the owner can never accidentally lock themselves out of
    // any feature.
    if (user.role === "owner") return true;
    if (!user.permissions) return false;
    return !!user.permissions[key];
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, loginScope, demoLogin, logout, setUser, claimPassword, hasPermission }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
